import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Heading, Text, Box, Flex, Toast } from '@vibe/core'
import { NavigationChevronLeft } from '@vibe/icons'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { PermissionGate } from '../components/common/PermissionGate'
import { JobDefinitionForm } from '../components/forms'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import type {
  JobDefinitionFormData,
  CreateJobDefinitionRequest,
} from '../types'
import { PermissionResource, PermissionAction } from '../types'

export const NewJobDefinition: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Create job definition
  const handleCreate = async (formData: JobDefinitionFormData) => {
    try {
      setLoading(true)

      const createData: CreateJobDefinitionRequest = {
        name: formData.name,
        puesto: formData.puesto,
        funciones: formData.funciones,
        perfil: {
          idiomas: formData.idiomas.filter(
            idioma => idioma.lenguaje.trim() && idioma.nivel.trim()
          ),
          estudios: formData.estudios.filter(
            estudio => estudio.nivel.trim() && estudio.detalle.trim()
          ),
          conocimientos: formData.conocimientos.filter(c => c.trim()),
        },
        requisitos: {
          indispensables: formData.requisitos_indispensables.filter(r =>
            r.trim()
          ),
        },
      }

      await apiService.createJobDefinition(createData)

      setToast({
        message: 'Definición de puesto creada exitosamente',
        type: 'positive',
      })

      // Navigate back to job definitions list after a short delay
      setTimeout(() => {
        navigate('/job-definitions')
      }, 2000)
    } catch (error) {
      console.error('Error creating job definition:', error)
      setToast({
        message: getErrorMessage(error),
        type: 'negative',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/job-definitions')
  }

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  return (
    <AuthGuard>
      <PermissionGate
        requiredPermission={{
          resource: PermissionResource.SOLICITUDES,
          action: PermissionAction.CREATE,
        }}
        fallback={
          <Layout>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <Text>
                  No tienes permisos para crear definiciones de puesto.
                </Text>
              </div>
            </div>
          </Layout>
        }
      >
        <Layout>
          <div className="space-y-6">
            {/* Toast notifications */}
            {toast && (
              <Toast
                open={true}
                onClose={() => setToast(null)}
                className="fixed top-4 right-4 z-50"
                type={toast.type}
              >
                {toast.message}
              </Toast>
            )}

            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Flex align="center" gap="medium" className="mb-4">
                <Button
                  kind="tertiary"
                  size="small"
                  onClick={handleCancel}
                  className="flex-shrink-0"
                >
                  <NavigationChevronLeft />
                </Button>
                <Box>
                  <Heading type="h1" className="mb-2">
                    Nueva Definición de Puesto
                  </Heading>
                  <Text color="secondary">
                    Crea una nueva definición de puesto reutilizable para
                    agilizar el proceso de reclutamiento
                  </Text>
                </Box>
              </Flex>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <JobDefinitionForm
                mode="create"
                onSubmit={handleCreate}
                onCancel={handleCancel}
              />
            </div>
          </div>
        </Layout>
      </PermissionGate>
    </AuthGuard>
  )
}

export default NewJobDefinition
